import React, { useEffect, useState, useCallback } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CCollapse,
  CFormCheck,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CInputGroup,
  CRow,
  CSmartTable,
  CSpinner,
} from '@coreui/react-pro'
import {
  cilCheckAlt,
  cilCheckCircle,
  cilNewspaper,
  cilSearch,
  cilX,
  cilXCircle,
} from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import CommunityPostsService from 'src/service/CommunityPostsService'
import { stripHtml } from 'src/utils/html'
import './CommunityPosts.css'

// The API stores post_type as a slug; these are the labels the admin sees.
const POST_TYPES = [
  { value: 'winner', label: 'Winner' },
  { value: 'round_winner', label: 'Round Winner' },
  { value: 'round_top_teams', label: 'Top Teams' },
  { value: 'player_of_round', label: 'Player of the Round' },
  { value: 'round_top_players', label: 'Top Players' },
  { value: 'ranking_update', label: 'Ranking Update' },
  { value: 'admin_announcement', label: 'Announcement' },
]

const POST_TYPE_COLORS = {
  admin_announcement: 'primary',
  winner: 'success',
  round_winner: 'info',
  round_top_teams: 'warning',
  player_of_round: 'danger',
  round_top_players: 'primary',
  ranking_update: 'secondary',
}

const SOURCE_COLORS = { system: 'success', admin: 'warning' }

const REACTIONS = [
  { key: 'like_count', label: 'Likes' },
  { key: 'fire_count', label: 'Fire' },
  { key: 'love_count', label: 'Love' },
  { key: 'clap_count', label: 'Clap' },
  { key: 'wow_count', label: 'Wow' },
]

const postTypeLabel = (type) =>
  POST_TYPES.find((item) => item.value === type)?.label || type || 'Unknown'

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-')
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-')

const StatCard = ({ icon, tone, label, value }) => (
  <CCard className="cp-stat-card h-100">
    <CCardBody className="d-flex align-items-center gap-3">
      <span className={`cp-stat-icon cp-stat-icon--${tone}`}>
        <CIcon icon={icon} size="lg" />
      </span>
      <div>
        <div className="cp-stat-label">{label}</div>
        <div className="cp-stat-value">{value ?? 0}</div>
      </div>
    </CCardBody>
  </CCard>
)

const AdminCommunityManager = () => {
  const [posts, setPosts] = useState(null)
  const [stats, setStats] = useState(null)
  const [selectedPosts, setSelectedPosts] = useState([])
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [alert, setAlert] = useState(null)
  const [filterPostType, setFilterPostType] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [details, setDetails] = useState([])

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await CommunityPostsService.getStats(filterPostType || undefined)
      if (response.status === 200) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [filterPostType])

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    setLoading(true)
    try {
      let paramsString = ''

      if (filterPostType) {
        paramsString += `post_type=${filterPostType}&`
      }

      if (searchTerm) {
        paramsString += `title=${encodeURIComponent(searchTerm)}&`
      }

      // Sorting
      if (columnSorter?.column) {
        paramsString += `sort_by=${columnSorter.column}&`
        paramsString += `sort_order=${columnSorter.state}&`
      }
      const offset = (Number(activePage) - 1) * Number(itemsPerPage)
      const pageNum = Number(activePage)

      const response = await CommunityPostsService.getCommunityPosts(
        filterPostType || '',
        offset,
        itemsPerPage,
        pageNum,
        paramsString,
      )

      if (response.status === 200) {
        setPosts(response.data)
      }
    } catch (error) {
      setAlert({
        color: 'danger',
        message: 'Failed to fetch posts',
      })
      console.error('Error fetching posts:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, filterPostType, searchTerm, columnSorter])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  // The title search hits the API on every keystroke otherwise.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setActivePage(1)
        setSearchTerm(searchInput)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, searchTerm])

  // Toggle post selection
  const togglePostSelection = (postId) => {
    setSelectedPosts((prev) =>
      prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId],
    )
  }

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedPosts.length === posts?.data?.length) {
      setSelectedPosts([])
    } else {
      setSelectedPosts(posts?.data?.map((post) => post.id) || [])
    }
  }

  // Update single post status
  const handleSingleStatusUpdate = async (postId, newStatus) => {
    setUpdating(true)
    try {
      const response = await CommunityPostsService.updatePostStatus(postId, newStatus)
      if (response.status === 200) {
        setAlert({
          color: 'success',
          message: `Post ${newStatus ? 'activated' : 'deactivated'} successfully!`,
        })
        fetchPosts()
        fetchStats()
      }
    } catch (error) {
      setAlert({
        color: 'danger',
        message: 'Failed to update post status',
      })
    } finally {
      setUpdating(false)
    }
  }

  // Bulk update status
  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedPosts.length === 0) {
      setAlert({
        color: 'warning',
        message: 'Please select at least one post',
      })
      return
    }

    setUpdating(true)
    try {
      const response = await CommunityPostsService.bulkUpdateStatus(selectedPosts, newStatus)
      if (response.status === 200) {
        setAlert({
          color: 'success',
          message: `${response.data.updated_count} post(s) ${
            newStatus ? 'activated' : 'deactivated'
          } successfully!`,
        })
        setSelectedPosts([])
        fetchPosts()
        fetchStats()
      }
    } catch (error) {
      setAlert({
        color: 'danger',
        message: 'Failed to update posts',
      })
    } finally {
      setUpdating(false)
    }
  }

  const toggleDetails = (index) => {
    const position = details.indexOf(index)
    let newDetails = details.slice()
    if (position !== -1) {
      newDetails.splice(position, 1)
    } else {
      newDetails = [...details, index]
    }
    setDetails(newDetails)
  }

  const allSelected = posts?.data?.length > 0 && selectedPosts.length === posts?.data?.length

  const columns = [
    {
      key: 'select',
      label: (
        <CFormCheck
          checked={!!allSelected}
          onChange={toggleSelectAll}
          id="selectAll"
          aria-label="Select all posts on this page"
        />
      ),
      filter: false,
      sorter: false,
    },
    { key: 'post_type', label: 'Post Type', filter: false, _style: { width: '12%' } },
    { key: 'title', label: 'Title / Description', filter: false, _style: { width: '28%' } },
    { key: 'source', label: 'Source', filter: false, _style: { width: '10%' } },
    { key: 'is_active', label: 'Status', filter: false, sorter: true, _style: { width: '12%' } },
    { key: 'created_at', label: 'Created', filter: false, _style: { width: '12%' } },
    { key: 'actions', label: 'Actions', filter: false, sorter: false, _style: { width: '1%' } },
  ]

  const scopedColumns = {
    select: (item) => (
      <td>
        <CFormCheck
          checked={selectedPosts.includes(item.id)}
          onChange={() => togglePostSelection(item.id)}
          id={`post-${item.id}`}
          aria-label={`Select post ${item.id}`}
        />
      </td>
    ),
    post_type: (item) => (
      <td>
        <CBadge
          color={POST_TYPE_COLORS[item.post_type] || 'secondary'}
          shape="rounded-pill"
          className="px-2 py-1"
        >
          {postTypeLabel(item.post_type)}
        </CBadge>
      </td>
    ),
    title: (item) => {
      // short_description carries rich text for announcements, so show its text, not markup.
      const description = stripHtml(item.short_description)
      return (
        <td>
          <div className="cp-title">
            <span className="cp-title-main">{item.title || 'Untitled post'}</span>
            <span className="cp-title-desc">
              {description
                ? description.length > 120
                  ? `${description.slice(0, 120)}…`
                  : description
                : 'No description'}
            </span>
          </div>
        </td>
      )
    },
    source: (item) => (
      <td>
        <CBadge
          color={SOURCE_COLORS[item.source] || 'info'}
          shape="rounded-pill"
          className="px-2 py-1"
        >
          {item.source || '-'}
        </CBadge>
      </td>
    ),
    is_active: (item) => (
      <td>
        <div className="cp-status">
          <CFormSwitch
            id={`switch-${item.id}`}
            checked={!!item.is_active}
            disabled={updating}
            onChange={(e) => handleSingleStatusUpdate(item.id, e.target.checked)}
            aria-label={item.is_active ? 'Deactivate post' : 'Activate post'}
          />
          <CBadge
            color={item.is_active ? 'success' : 'secondary'}
            shape="rounded-pill"
            className="px-2 py-1"
          >
            {item.is_active ? 'Active' : 'Inactive'}
          </CBadge>
        </div>
      </td>
    ),
    created_at: (item) => <td>{formatDate(item.created_at)}</td>,
    actions: (item) => (
      <td>
        <CButton color="primary" variant="outline" size="sm" onClick={() => toggleDetails(item.id)}>
          {details.includes(item.id) ? 'Hide' : 'View'}
        </CButton>
      </td>
    ),
    details: (item) => (
      <CCollapse visible={details.includes(item.id)}>
        <div className="cp-details">
          <div className="cp-detail-grid">
            <div className="cp-detail">
              <span className="cp-detail-label">Post ID</span>
              <span className="cp-detail-value">{item.id}</span>
            </div>
            <div className="cp-detail">
              <span className="cp-detail-label">Post Type</span>
              <span className="cp-detail-value">{postTypeLabel(item.post_type)}</span>
            </div>
            <div className="cp-detail">
              <span className="cp-detail-label">Source</span>
              <span className="cp-detail-value">{item.source || '-'}</span>
            </div>
            {item.round_number && (
              <div className="cp-detail">
                <span className="cp-detail-label">Round</span>
                <span className="cp-detail-value">{item.round_number}</span>
              </div>
            )}
            <div className="cp-detail">
              <span className="cp-detail-label">Status</span>
              <span className="cp-detail-value">{item.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            <div className="cp-detail">
              <span className="cp-detail-label">Created</span>
              <span className="cp-detail-value">{formatDateTime(item.created_at)}</span>
            </div>
            <div className="cp-detail">
              <span className="cp-detail-label">Updated</span>
              <span className="cp-detail-value">{formatDateTime(item.updated_at)}</span>
            </div>
          </div>

          <div className="cp-detail mt-3">
            <span className="cp-detail-label">Reactions</span>
            <div className="cp-reactions mt-1">
              {REACTIONS.map((reaction) => (
                <span className="cp-reaction" key={reaction.key}>
                  {reaction.label}
                  <strong>{item[reaction.key] ?? 0}</strong>
                </span>
              ))}
            </div>
          </div>

          <div className="cp-detail mt-3">
            <span className="cp-detail-label">Payload</span>
            <pre className="cp-payload">{JSON.stringify(item.payload, null, 2)}</pre>
          </div>
        </div>
      </CCollapse>
    ),
  }

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Community Posts</h1>
        <p className="ge-page-subtitle">
          Review the posts generated for your community feed and control which ones members see.
        </p>
      </div>

      {alert && (
        <CAlert color={alert.color} className="mb-3" onClose={() => setAlert(null)} dismissible>
          {alert.message}
        </CAlert>
      )}

      <CRow className="g-3 mb-4">
        <CCol xs={12} sm={6} xl={4}>
          <StatCard
            icon={cilNewspaper}
            tone="violet"
            label="Total Posts"
            value={stats?.total_posts}
          />
        </CCol>
        <CCol xs={12} sm={6} xl={4}>
          <StatCard
            icon={cilCheckCircle}
            tone="green"
            label="Active Posts"
            value={stats?.active_posts}
          />
        </CCol>
        <CCol xs={12} sm={6} xl={4}>
          <StatCard
            icon={cilXCircle}
            tone="red"
            label="Inactive Posts"
            value={stats?.inactive_posts}
          />
        </CCol>
      </CRow>

      <CCard className="cp-panel">
        <CCardBody>
          <div className="cp-panel-toolbar">
            <div>
              <h2 className="cp-panel-title">Community Posts</h2>
              <p className="cp-panel-note">
                {posts?.total ?? 0} {posts?.total === 1 ? 'post' : 'posts'}
                {filterPostType ? ` · ${postTypeLabel(filterPostType)}` : ''}
              </p>
            </div>
            <div className="cp-panel-actions">
              <CFormSelect
                className="cp-type-select"
                aria-label="Filter by post type"
                value={filterPostType}
                onChange={(e) => {
                  setFilterPostType(e.target.value)
                  setActivePage(1)
                }}
              >
                <option value="">All Types</option>
                {POST_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </CFormSelect>

              <CInputGroup className="ge-search">
                <span className="ge-search-icon">
                  <CIcon icon={cilSearch} />
                </span>
                <CFormInput
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by title..."
                  aria-label="Search posts by title"
                />
              </CInputGroup>
            </div>
          </div>

          {selectedPosts.length > 0 && (
            <div className="cp-bulk-bar">
              <span className="cp-bulk-count">
                {selectedPosts.length} {selectedPosts.length === 1 ? 'post' : 'posts'} selected
              </span>
              <div className="cp-bulk-actions">
                <CButton
                  color="success"
                  size="sm"
                  onClick={() => handleBulkStatusUpdate(true)}
                  disabled={updating}
                >
                  {updating ? (
                    <CSpinner component="span" size="sm" className="me-2" />
                  ) : (
                    <CIcon icon={cilCheckAlt} className="me-2" />
                  )}
                  Activate
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  onClick={() => handleBulkStatusUpdate(false)}
                  disabled={updating}
                >
                  {updating ? (
                    <CSpinner component="span" size="sm" className="me-2" />
                  ) : (
                    <CIcon icon={cilX} className="me-2" />
                  )}
                  Deactivate
                </CButton>
                <CButton
                  color="secondary"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPosts([])}
                  disabled={updating}
                >
                  Clear
                </CButton>
              </div>
            </div>
          )}

          <div className="ge-datatable ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              sorterValue={columnSorter}
              noItemsLabel={
                searchTerm || filterPostType
                  ? 'No posts match these filters'
                  : 'No community posts yet'
              }
              onSorterChange={(sorter) => {
                if (
                  columnSorter?.column === sorter?.column &&
                  columnSorter?.state === sorter?.state
                ) {
                  return
                }

                setColumnSorter(sorter)
                setActivePage(1)
              }}
              scopedColumns={scopedColumns}
              items={posts?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage: activePage,
                pages: Math.ceil(posts?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(Number(page) || 1)}
              onItemsPerPageChange={(items) => {
                setItemsPerPage(Number(items) || 10)
                setActivePage(1)
              }}
            />
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

export default AdminCommunityManager
